import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useCompanyContext } from "../hooks/useCompanyContext";
import FileUpload from "./FileUpload";

// Ordernummers uniform vergelijken
const normalizeOrderNo = (value) =>
  String(value ?? "").trim().toUpperCase();

// Datum omzetten naar YYYY-MM-DD
const getDateKey = (value) => {
  if (!value) return "";

  const dateString = String(value);
  const isoMatch = dateString.match(/^\d{4}-\d{2}-\d{2}/);

  if (isoMatch) {
    const dateKey = isoMatch[0];

    if (dateKey === "0001-01-01" || dateKey === "0000-00-00") {
      return "";
    }

    return dateKey;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

// Datum weergeven als DD-MM-YYYY
const formatDeliveryDate = (dateKey) => {
  if (!dateKey) return "N.v.t.";

  const [year, month, day] = dateKey.split("-");

  return `${day}-${month}-${year}`;
};

const FileList = ({ user }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("GlobalGap");

  // WU-66: Eén zoekveld voor ordernummer en leverdatum
  const [search, setSearch] = useState("");

  const { company, t } = useCompanyContext();

  const tabs = [
    { key: "GlobalGap", label: t.globalGap },
    { key: "Partij informatie", label: t.partijInformatie },
    { key: "Algemeen", label: t.algemeen },
  ];

  useEffect(() => {
    const token = localStorage.getItem("token");
    let active = true;

    setSearch("");

    if (!company?.Companyid) {
      console.error("❌ Geen geselecteerd bedrijf gevonden");
      setFiles([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    console.log(
      "📥 Ophalen bestanden voor bedrijf:",
      company.Companyid
    );

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/documents?companyid=${company.Companyid}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {
        if (!active) return;

        console.log("✅ Bestanden ontvangen:", res.data);

        const sortedFiles = [...res.data].sort((a, b) => {
          const order = {
            GlobalGap: 1,
            "Partij informatie": 2,
            Algemeen: 3,
          };

          return (
            (order[a.category] || 4) -
            (order[b.category] || 4)
          );
        });

        setFiles(sortedFiles);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;

        console.error(
          "❌ Fout bij ophalen bestanden:",
          err.response?.data || err.message
        );

        setFiles([]);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [company?.Companyid]);

  // --------------------------------------------------
  // WU-66: Leverdatums koppelen aan ordernummers
  // --------------------------------------------------

  const orderDeliveryDates = useMemo(() => {
    const deliveryMap = new Map();

    const orders = company?.orders || [];

    orders.forEach((order) => {
      const orderNo = normalizeOrderNo(order.No);

      if (!orderNo) return;

      deliveryMap.set(
        orderNo,
        getDateKey(order.IA_PromisedDeliveryDate)
      );
    });

    return deliveryMap;
  }, [company?.orders]);

  const getFileDeliveryDate = (file) => {
    const orderNo = normalizeOrderNo(file.order_no);

    if (!orderNo) return "";

    return orderDeliveryDates.get(orderNo) || "";
  };

  // --------------------------------------------------
  // WU-66: Zoeken op ordernummer OF leverdatum
  // --------------------------------------------------

  const filteredFiles = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return files.filter((file) => {
      // Bestaande tab-filter
      if (file.category !== activeTab) {
        return false;
      }

      // Zoekfunctie alleen toepassen bij GlobalGap
      if (activeTab !== "GlobalGap") {
        return true;
      }

      if (!searchTerm) return true;

      // Ordernummer
      const orderNo = normalizeOrderNo(file.order_no)
        .toLowerCase();

      // Dynamisch opgehaalde leverdatum
      const deliveryDate =
        orderDeliveryDates.get(
          normalizeOrderNo(file.order_no)
        ) || "";

      // Nederlandse datumnotatie
      const formattedDate = deliveryDate
        ? formatDeliveryDate(deliveryDate).toLowerCase()
        : "";

      // Alternatieve notatie met /
      const slashDate = formattedDate.replace(/-/g, "/");

      // Zoeken op een gedeelte van het ordernummer
      // of op een gedeelte van de leverdatum
      return (
        orderNo.includes(searchTerm) ||
        formattedDate.includes(searchTerm) ||
        slashDate.includes(searchTerm) ||
        deliveryDate.includes(searchTerm)
      );
    });
  }, [files, activeTab, search, orderDeliveryDates]);

  // --------------------------------------------------
  // Download
  // --------------------------------------------------

  const handleDownload = async (id, filename) => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/documents/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("📥 Bestand ontvangen:", res.data);

      if (!res.data.file) {
        console.error("❌ Geen bestand ontvangen");
        return;
      }

      const byteCharacters = atob(res.data.file);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", filename);

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("❌ Download mislukt:", error);
    }
  };

  // --------------------------------------------------
  // Upload
  // --------------------------------------------------

  const handleUploadSuccess = (newFile) => {
    setFiles((prevFiles) => {
      const updatedFiles = [newFile, ...prevFiles];

      return updatedFiles.sort((a, b) => {
        const order = {
          GlobalGap: 1,
          "Partij informatie": 2,
          Algemeen: 3,
        };

        return (
          (order[a.category] || 4) -
          (order[b.category] || 4)
        );
      });
    });
  };

  // --------------------------------------------------
  // Verwijderen
  // --------------------------------------------------

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Weet je zeker dat je dit bestand wilt verwijderen?"
      )
    )
      return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/documents/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setFiles((prevFiles) =>
        prevFiles.filter((file) => file._id !== id)
      );
    } catch (error) {
      console.error("Verwijderen mislukt:", error);
    }
  };

  if (loading) return <p>Bestanden laden...</p>;

  return (
    <div>
      {/* Alleen medewerkers mogen uploaden */}
      {user?.roles?.includes("medewerker") && (
        <div className="file-upload">
          <FileUpload
            user={user}
            onUploadSuccess={handleUploadSuccess}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {tabs.map((tab) => {
          const count = files.filter(
            (file) => file.category === tab.key
          ).length;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={
                activeTab === tab.key ? "active-tab" : ""
              }
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {/* WU-66: Eén zoekbalk voor GlobalGap */}
      {activeTab === "GlobalGap" && (
        <div
          className="search-container"
          style={{ margin: "15px 0" }}
        >
          <input
            type="text"
            placeholder={t.zoekPlaceholderOrderinformatie}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      {/* Geen bestanden melding */}
      {filteredFiles.length === 0 ? (
        <p>
          {activeTab === "GlobalGap" && search
            ? "Geen documenten gevonden voor deze zoekopdracht."
            : "Er zijn geen bestanden beschikbaar in deze categorie."}
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t.soort}</th>
              <th>{t.naam}</th>
              <th>
                {t.ordernummer} / {t.titel}
              </th>

              {/* WU-66: Dynamische leverdatum */}
              <th>{t.leverdatum || "Leverdatum"}</th>

              {/* Bestaande uploaddatum */}
              <th>{t.datum}</th>

              <th>{t.actie}</th>
            </tr>
          </thead>

          <tbody>
            {filteredFiles.map((file) => (
              <tr key={file._id}>
                <td>
                  {file.category || "Algemeen"}
                </td>

                <td>{file.filename}</td>

                <td>
                  {file.order_no || file.title || "N.v.t."}
                </td>

                {/* WU-66: Leverdatum ophalen uit order */}
                <td>
                  {formatDeliveryDate(
                    getFileDeliveryDate(file)
                  )}
                </td>

                {/* Bestaande uploaddatum */}
                <td>
                  {new Date(
                    file.uploaded_at
                  ).toLocaleDateString("nl-NL", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </td>

                <td>
                  {user?.roles?.includes("admin") && (
                    <button
                      onClick={() =>
                        handleDownload(
                          file._id,
                          file.filename
                        )
                      }
                      style={{
                        marginRight: "15px",
                      }}
                    >
                      ✓ Goedkeuren
                    </button>
                  )}

                  {user?.roles?.includes("admin") && (
                    <button
                      onClick={() =>
                        handleDownload(
                          file._id,
                          file.filename
                        )
                      }
                      style={{
                        marginRight: "15px",
                      }}
                    >
                      X Afkeuren
                    </button>
                  )}

                  <button
                    onClick={() =>
                      handleDownload(
                        file._id,
                        file.filename
                      )
                    }
                    style={{
                      marginRight: "15px",
                    }}
                  >
                    📥 {t.download}
                  </button>

                  {user?.roles?.includes("medewerker") && (
                    <button
                      onClick={() =>
                        handleDelete(file._id)
                      }
                    >
                      🗑 Verwijderen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FileList;